import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { useTranslation } from "react-i18next";
import { AttributeTabsCustom  } from "./AttributeTabs.style";
import { TabsSkeleton } from "./TabsSkeleton.component";
import { FILTERS } from "../../consts";
import { useUserAttributes, useUserProgressWithAttributes/*, useAttributesQuery*/ } from "../../queries";
import { useAttributeTabStore } from "../../stores/attributeTabStore";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";


type IFilterTabs = {
  children?: React.ReactNode;
  onClickHandler: (value: string) => void;
}

export type Attribute = {
  attributeId: number|string
  corporateId: number
  attribute: string
  userDeleted: boolean
  creationDate: string
  updateDate: string
  forYou: boolean
  exploreCarousel: boolean
  deleted: boolean
}

type NewAttribute = {
    attributeName: string,
    attributeId: number|string
};
export const AttributeTabs = ({ onClickHandler, children }: IFilterTabs) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { attributesArray, translatedAttribute } = useUserAttributes();
  const { setSelectedTab, selectedTab, resetAttribute } = useAttributeTabStore();
  const { isFetching, isError } = useUserProgressWithAttributes();
  //const { data: enrollmentAttributes } = useAttributesQuery(); // TODO: abilitare quando ci sarà possibilità di filtrare per i soli attributi per cui l'utente ha LO
  const { customLogicFe } = getEnvironmentVariables();

  let attributesWithDefault: NewAttribute[] = [];
  // Tab sempre presente
  const totalTab: NewAttribute = {
    attributeId: "total",
    attributeName: t("filters_all")
  };

  const mandatoryTab: NewAttribute = {
    attributeId: "mandatory",
    attributeName: t("mandatory")
  };

  if (isError || !attributesArray || attributesArray.length === 0) {
    attributesWithDefault = [
      totalTab,
      ...(checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideMandatoryForYou" }) ? [] : [mandatoryTab])
    ];
  } else {

    const firstOriginalAttribute =  translatedAttribute  ? attributesArray[0] : null;

    const otherFilteredAttributes = translatedAttribute ? attributesArray.slice(1):attributesArray;  // Prendo tutti gli attributi filtrati tranne il primo (se esiste)

    attributesWithDefault = [
      totalTab,
      ...(firstOriginalAttribute ? [firstOriginalAttribute] : []),
      ...(checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideMandatoryForYou" }) ? [] : [mandatoryTab]),
      ...otherFilteredAttributes
    ];
  }


  const handleChange = (filter: NewAttribute) => {
    const value = filter.attributeId.toString();
    const widgetFilter = filter.attributeId.toString();
    onClickHandler(value);

    if (value === FILTERS.ALL) {
      resetAttribute();
      return;
    }
    setSelectedTab(widgetFilter);

  };

  const a11yProps = (id: string) => {
    return {
      id: `filter-tab-${id}`,
      "aria-controls": `filter-tabpanel-${id}`
    };
  };

  // Se stiamo ancora caricando e non ci sono attributi, mostra skeleton
  if(isFetching && (!attributesWithDefault || attributesWithDefault.length === 0)) {
    return <TabsSkeleton />;
  }

  // Se non ci sono attributi da mostrare (neanche quelli essenziali), mostra solo i children
  if(!attributesWithDefault || attributesWithDefault.length === 0) {
    return <div style={ { marginTop:"0.5rem" } }>{ children }</div>;
  }

  return (
    <>
      <AttributeTabsCustom
        sx={ {
          ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
          ".Mui-selected": { color: theme.customColors.accentVariantA },
          fontFamily: "Inter"
        } }
        TabIndicatorProps={ { style: { background: theme.customColors.accentVariantA, borderRadius: "1.5rem" } } }
        variant="scrollable"
        scrollButtons={ true }
        allowScrollButtonsMobile
        value={ selectedTab.toString() }
        aria-label="filter tabs for you page">
        { (attributesWithDefault||[]).length > 1 ? attributesWithDefault.map((filter: NewAttribute, index) => (
          <Tab
            tabIndex={ 0 }
            aria-label={ filter.attributeName }
            sx={ {
              textTransform: "none",
              fontSize: "0.875rem",
              letterSpacing: "0.0313em",
              fontWeight: 600,
              fontStyle: "semibold",
              height: "1.5rem",
              minWidth: "3rem",
              marginX:"0.35rem",
              lineHeight: "1.5rem",
              borderBottom: `2px solid ${theme.customColors.border}`,
              "&.Mui-focusVisible": {
                border: `1px solid ${theme.customColors.accentVariantA}`
              }
            } }
            key={ index }
            label={ filter.attributeName }
            value={ filter.attributeId.toString() }
            onClick={ () => handleChange(filter) }
            { ...a11yProps(filter.attributeId.toString()) }
          />
        )):null }
      </AttributeTabsCustom>
      { (attributesWithDefault||[]).map((filter: NewAttribute) => (
        <div
          tabIndex={ 0 }
          aria-label={ filter.attributeName }
          key={ filter.attributeId.toString() }
          role="tabpanel"
          hidden={ filter.attributeId.toString() !== selectedTab }
          id={ `filter-tabpanel-${filter.attributeId.toString()}` }
          aria-labelledby={ `filter-tab-${filter.attributeId.toString()}` }
        >
          { selectedTab === filter.attributeId.toString() && <Box>{ children }</Box> }
        </div>
      ))
      }
    </>
  );
};
